/* eslint-disable no-console */
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect, useState, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, TF} from '../../constants/Common';
import InformativeText from '../../constants/InformativeText';
import {Itabs} from '../../interface/Tab';
import TranslatedText from '../../libraries/I18N/TranslatedText';
import {sortBy} from '../../shared/commonFunctions';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import FormListComp from '../DrawerForm/Utils/FormListComp';
import Loader from '../Loader/Loader';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';
import Tabs from '../Tabs/Tabs';

import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {
  RawMaterialKey,
  RawMaterialLabel
} from './Constants/Products/RawMaterialKeyAndLabel';
import {unitTypes} from './interface/unitChange';
import changeUnit from './Utils/changeUnit';
import {filterSelectOptions} from './Utils/commonFunctions';
import CapGoodsSupplierSpec from './Utils/TableListForm/CapGoodsSupplierSpec';

const RawAverageForm: React.FC<{
  data: any;
  setSubmitDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  isAverage?: boolean;
  rawMaterialFilter: any;
  suppliers: any[];
  currentStep?: number;
  setFormSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  setIsFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isFormLoading: boolean;
  defaultDisplayStep?: boolean;
}> = ({
  data,
  isAverage = true,
  rawMaterialFilter,
  suppliers,
  currentStep,
  setFormSelectedYear,
  setIsFormLoading,
  isFormLoading,
  defaultDisplayStep = false
}) => {
  const FormInstance = useFormInstance();
  const [searchParam] = useSearchParams();
  const {setFields, getFieldValue} = FormInstance;
  const companyUnits = useCompanyStore.getState().preUnits;

  const {facilityFilter, productFilter, rawFilter} = rawMaterialFilter;

  const rawInfo = InformativeText?.Product?.raw_material;

  const [rawMaterialSel, setRawMaterialSel] = useState<any>(unitTypes.WEIGHT);

  const [selectedFacility, setSelectedFacility] = useState<any>({});

  const [selectedRawMaterial, setSelectedRawMaterial] = useState<any>({});

  useEffect(() => {
    setRawMaterialSel(
      rawFilter?.find((item: any) => {
        return (
          item.value ===
          (getFieldValue(RawMaterialKey.RAW_MATERIAL) ??
            data?.[RawMaterialKey.RAW_MATERIAL])
        );
      })
    );
  }, [getFieldValue(RawMaterialKey.RAW_MATERIAL)]);

  useEffect(() => {
    setSelectedFacility(
      facilityFilter?.find((item: any) => {
        return (
          item?.value ===
          (getFieldValue(RawMaterialKey.FACILITY) ??
            data?.[RawMaterialKey.FACILITY])
        );
      })?.facilityId
    );
    setSelectedRawMaterial(
      rawFilter?.find((item: any) => {
        return (
          item?.value ===
          (getFieldValue(RawMaterialKey.RAW_MATERIAL) ??
            data?.[RawMaterialKey.RAW_MATERIAL])
        );
      })?.facilityId
    );
  }, [rawMaterialFilter, isFormLoading]);

  const yearFilter = yearSelect();

  const treated: MYConfig[] = useMemo(() => {
    return [
      {
        mainTitle: {
          key: RawMaterialKey.WEIGHT_TF,
          title: RawMaterialLabel.WEIGHT_TF
        },
        unit: changeUnit(rawMaterialSel?.state)
      },
      {
        mainTitle: {
          key: RawMaterialKey.COST_TF,
          title: RawMaterialLabel.COST_TF
        },
        unit: companyUnits.currency
      }
    ];
  }, [rawMaterialSel]);

  const rawRecycleTabs: Itabs[] = [
    {
      id: RawMaterialKey.RECYCLE.recycle,
      name: (
        <TranslatedText
          translationKey={RawMaterialLabel.RECYCLE.recycle}
          tooltip={
            isAverage
              ? rawInfo?.average?.recycled
              : rawInfo?.supplierSpec?.recycled
          }
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            getFieldValue(RawMaterialKey.RECYCLE_DATA)?.find((item: any) => {
              return item?.recyclingType === RawMaterialKey.RECYCLE.recycle;
            })?.[TF]
          }
          disposalMethod={{
            value: true,
            type: RawMaterialKey.RECYCLE.recycle,
            key: RawMaterialKey.RECYCLE_METHOD
          }}
          nested={RawMaterialKey.RECYCLE_DATA}
        />
      ),
      forceRender: true
    },
    {
      id: RawMaterialKey.RECYCLE.reuse,
      name: (
        <TranslatedText
          translationKey={RawMaterialLabel.RECYCLE.reuse}
          tooltip={
            isAverage ? rawInfo?.average?.reused : rawInfo?.supplierSpec?.reused
          }
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            (getFieldValue(RawMaterialKey.RECYCLE_DATA) ?? [])?.find(
              (item: any) => {
                return item?.recyclingType === RawMaterialKey.RECYCLE.reuse;
              }
            )?.[TF] as MYData[]
          }
          disposalMethod={{
            value: true,
            type: RawMaterialKey.RECYCLE.reuse,
            key: RawMaterialKey.RECYCLE_METHOD
          }}
          nested={RawMaterialKey.RECYCLE_DATA}
        />
      ),
      forceRender: true
    },
    {
      id: RawMaterialKey.RECYCLE.fresh,
      name: (
        <TranslatedText
          translationKey={RawMaterialLabel.RECYCLE.fresh}
          tooltip={
            isAverage ? rawInfo?.average?.fresh : rawInfo?.supplierSpec?.fresh
          }
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            (getFieldValue(RawMaterialKey.RECYCLE_DATA) ?? [])?.find(
              (item: any) => {
                return item?.recyclingType === RawMaterialKey.RECYCLE.fresh;
              }
            )?.[TF] as MYData[]
          }
          disposalMethod={{
            value: true,
            type: RawMaterialKey.RECYCLE.fresh,
            key: RawMaterialKey.RECYCLE_METHOD
          }}
          nested={RawMaterialKey.RECYCLE_DATA}
        />
      ),
      forceRender: true
    }
  ];

  return (
    <>
      {isFormLoading ? (
        <Loader />
      ) : (
        <>
          {(currentStep === 0 || defaultDisplayStep) && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={yearKeyLabel.key}
                  label={yearKeyLabel.label}
                  hidden={defaultDisplayStep}
                  labelCol={{span: 24}}
                  initialValue={searchParam.get('selectedYear')}
                >
                  <Select
                    placeholder={yearKeyLabel.label}
                    options={yearFilter}
                    suffixIcon={<YearCalender />}
                    disabled={getFieldValue('_id') !== undefined}
                    onChange={(e) => {
                      setFormSelectedYear(e);
                      setIsFormLoading(true);
                      if (getFieldValue(RawMaterialKey.PRODUCT_ID)) {
                        setFields([
                          {
                            name: RawMaterialKey.PRODUCT_ID,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      if (getFieldValue(RawMaterialKey.FACILITY)) {
                        setFields([
                          {
                            name: RawMaterialKey.FACILITY,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      if (getFieldValue(RawMaterialKey.RAW_MATERIAL)) {
                        setFields([
                          {
                            name: RawMaterialKey.RAW_MATERIAL,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      if (
                        !isAverage &&
                        getFieldValue(RawMaterialKey.ADD_SUPPLIER)
                      ) {
                        setFields([
                          {
                            name: RawMaterialKey.ADD_SUPPLIER,
                            errors: [CommonErrors.selectAgain],
                            value: [{[RawMaterialKey.SUPPLIER_NAME]: undefined}]
                          }
                        ]);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={RawMaterialKey.PRODUCT_ID}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(
                        RawMaterialLabel.PRODUCT_ID
                      )
                    }
                  ]}
                  label={RawMaterialLabel.PRODUCT_ID}
                  labelCol={{span: 24}}
                >
                  <Select
                    showSearch
                    className="select"
                    placeholder={`Choose ${RawMaterialLabel.PRODUCT_ID}`}
                    options={sortBy(productFilter, 'label', 'asc')}
                    filterOption={filterSelectOptions}
                    onChange={() => {
                      if (getFieldValue(RawMaterialKey.FACILITY)) {
                        setFields([
                          {
                            name: RawMaterialKey.FACILITY,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      if (getFieldValue(RawMaterialKey.RAW_MATERIAL)) {
                        setFields([
                          {
                            name: RawMaterialKey.RAW_MATERIAL,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      if (
                        !isAverage &&
                        getFieldValue(RawMaterialKey.ADD_SUPPLIER)
                      ) {
                        setFields([
                          {
                            name: RawMaterialKey.ADD_SUPPLIER,
                            errors: [CommonErrors.selectAgain],
                            value: [{[RawMaterialKey.SUPPLIER_NAME]: undefined}]
                          }
                        ]);
                      }
                    }}
                    disabled={data !== undefined}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item dependencies={[RawMaterialKey.PRODUCT_ID]} noStyle>
                  {({getFieldValue}) => {
                    return (
                      <Form.Item
                        name={RawMaterialKey.FACILITY}
                        rules={[
                          {
                            required: true,
                            message: CommonErrors.mandatory(
                              RawMaterialLabel.FACILITY
                            )
                          }
                        ]}
                        label={RawMaterialLabel.FACILITY}
                        labelCol={{span: 24}}
                      >
                        <Select
                          showSearch
                          className="select"
                          filterOption={filterSelectOptions}
                          placeholder={`Choose ${RawMaterialLabel.FACILITY}`}
                          options={sortBy(
                            facilityFilter?.filter((item: any) => {
                              return (
                                item?.[RawMaterialKey.PRODUCT_ID] ===
                                getFieldValue(RawMaterialKey.PRODUCT_ID)
                              );
                            }),
                            'label',
                            'asc'
                          )}
                          disabled={data !== undefined}
                          onChange={(_, main) => {
                            setSelectedFacility(main?.facilityId);
                            if (getFieldValue(RawMaterialKey.RAW_MATERIAL)) {
                              setFields([
                                {
                                  name: RawMaterialKey.RAW_MATERIAL,
                                  errors: [CommonErrors.selectAgain],
                                  value: undefined
                                }
                              ]);
                            }
                            if (
                              !isAverage &&
                              getFieldValue(RawMaterialKey.ADD_SUPPLIER)
                            ) {
                              setFields([
                                {
                                  name: RawMaterialKey.ADD_SUPPLIER,
                                  errors: [CommonErrors.selectAgain],
                                  value: [
                                    {[RawMaterialKey.SUPPLIER_NAME]: undefined}
                                  ]
                                }
                              ]);
                            }
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item dependencies={[RawMaterialKey.PRODUCT_ID]} noStyle>
                  {({getFieldValue}) => {
                    return (
                      <Form.Item
                        name={RawMaterialKey.RAW_MATERIAL}
                        rules={[
                          {
                            required: true,
                            message: CommonErrors.mandatory(
                              RawMaterialLabel.RAW_MATERIAL
                            )
                          }
                        ]}
                        label={RawMaterialLabel.RAW_MATERIAL}
                        labelCol={{span: 24}}
                      >
                        <Select
                          showSearch
                          className="select"
                          filterOption={filterSelectOptions}
                          placeholder={`Choose ${RawMaterialLabel.RAW_MATERIAL}`}
                          options={sortBy(
                            rawFilter?.filter((item: any) => {
                              return (
                                item?.[RawMaterialKey.PRODUCT_ID] ===
                                getFieldValue(RawMaterialKey.PRODUCT_ID)
                              );
                            }),
                            'label',
                            'asc'
                          )}
                          onChange={(_, options: any) => {
                            setSelectedRawMaterial(options?.facilityId);
                            setRawMaterialSel(options);
                            if (
                              !isAverage &&
                              getFieldValue(RawMaterialKey.ADD_SUPPLIER)
                            ) {
                              setFields([
                                {
                                  name: RawMaterialKey.ADD_SUPPLIER,
                                  errors: [CommonErrors.selectAgain],
                                  value: [
                                    {[RawMaterialKey.SUPPLIER_NAME]: undefined}
                                  ]
                                }
                              ]);
                            }
                          }}
                          disabled={data !== undefined}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              {!isAverage && (
                <FormListComp
                  mainName={RawMaterialKey.ADD_SUPPLIER}
                  formComponent={
                    <CapGoodsSupplierSpec
                      supplier={suppliers?.filter((item) => {
                        return item?.data?.some((innerItem: any) => {
                          return (
                            innerItem?.facility?.includes(
                              selectedFacility ?? ''
                            ) &&
                            innerItem?.rawMaterialsSupplied?.includes(
                              selectedRawMaterial ?? ''
                            )
                          );
                        });
                      })}
                    />
                  }
                />
              )}
            </>
          )}
          {(currentStep === 1 || defaultDisplayStep) && (
            <>
              <Tabs
                array={rawRecycleTabs}
                type="card"
                destroy={false}
                slide={true}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default RawAverageForm;
